import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLLoggedInDriverQuery } from "store/services/driverApi";
import { useLazyGetDriverShipmentLoadingShipmentsQuery } from "store/services/collectionApi";
import { useLazySearchBranchQuery } from "store/services/branchApi";
import { useAuth } from "hooks";

const INITIAL_STATE = {
    shipmentId: [],
    driverId: "",
    sourceBranch: null,
    vehicle_id: "",
    isActive: true,
};

const DriverShipmentDestinationLoadingForm = ({
    isBusy = false,
    freeloadingDetail = null,
    onSubmit = () => Promise.resolve({}),
}) => {
    const navigate = useNavigate();
    const { viewEntity } = useAuth();
    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity || "",
            account: viewEntity?.entity?.account || "",
        },
    });
    const _watchDriver = useWatch({ control, name: "driverId" });

    // RTK Queries
    const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
        useLazyGetDDLLoggedInDriverQuery();
    const [fetchShipments, {
        data: shipmentsData,
        isLoading: loadingShipments,
        isFetching: isFetchingShipments,
    }] = useLazyGetDriverShipmentLoadingShipmentsQuery();
    const [searchBranch, {
        data: branchOptions,
        isFetching: isSearchingBranch,
    }] = useLazySearchBranchQuery();

    const [branchInput, setBranchInput] = useState("");

    // ─── Derived Data ────────────────────────────────────────────────────────
    const allShipments = useMemo(() => shipmentsData?.shipments || [], [shipmentsData]);

    const isLoadingData = loadingShipments || isFetchingShipments;

    // ─── Data Loaders ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!viewEntity?.entity?._id) return;
        const accountId = viewEntity.entity.account._id;
        const entityId = viewEntity.entity._id;
        fetchDrivers({ account: accountId, entity: entityId });
        fetchShipments({ account: accountId, entity: entityId });
    }, [viewEntity, fetchDrivers, fetchShipments]);

    // Debounced branch search (min 3 characters)
    useEffect(() => {
        if (!viewEntity?.entity?._id) return;
        const trimmed = branchInput.trim();
        if (trimmed.length < 3) return;

        const accountId = viewEntity.entity.account._id;
        const entityId = viewEntity.entity._id;
        const handle = setTimeout(() => {
            searchBranch({ account: accountId, entity: entityId, q: trimmed });
        }, 300);
        return () => clearTimeout(handle);
    }, [branchInput, viewEntity, searchBranch]);

    useEffect(() => {
        if (drivers?.length > 0 && !freeloadingDetail) {
            const d = drivers[0];
            setValue("driverId", d);
            if (d.assigned_vehicle_ids?.length > 0) setValue("vehicle_id", d.assigned_vehicle_ids[0]);
        }
    }, [drivers, freeloadingDetail, setValue]);

    // ─── Submit ──────────────────────────────────────────────────────────────
    const handleOnSubmit = async (data) => {
        await onSubmit({
            account: viewEntity.entity.account._id,
            entity: viewEntity.entity._id,
            shipmentId: data.shipmentId.map((s) => s._id),
            driverId: data.driverId._id,
            vehicle_id: data.vehicle_id._id,
            isActive: data.isActive,
            loadedBranch: data.sourceBranch?._id ?? null,
        });
    };

    // ─── Render ──────────────────────────────────────────────────────────────
    return (
        <AppForm loading={isBusy || isLoadingData}>
            <AppForm.Content>
                <Grid container spacing={4}>
                    {/* Loaded Branch — independent name search, sets loadedBranch */}
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="sourceBranch"
                            control={control}
                            rules={{ required: "Loaded branch is required." }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    label="Search for Branch"
                                    required
                                    value={value || null}
                                    onChange={(_, data) => onChange(data)}
                                    onInputChange={(_, val) => setBranchInput(val)}
                                    getOptionLabel={(o) =>
                                        o ? `${o.name ?? ""} (${o.code ?? ""})`.trim() : ""
                                    }
                                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                    options={branchOptions || []}
                                    loading={isSearchingBranch}
                                    filterOptions={(x) => x}
                                    error={!!errors?.sourceBranch}
                                    helperText={errors?.sourceBranch?.message}
                                />
                            )}
                        />
                    </Grid>
                    {/* Shipment picker — always shows all shipments */}
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="shipmentId"
                            control={control}
                            rules={{ required: "Shipment is required." }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth multiple required
                                    label="Shipment ID / Reference"
                                    value={value || []}
                                    onChange={(_, data) => onChange(data)}
                                    getOptionLabel={(o) =>
                                        `${o.shipmentId}${o?.reference ? ` | ${o.reference}` : ""} - ${o?.destinationBranch?.code ?? ""}`
                                    }
                                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                    options={allShipments}
                                    loading={isLoadingData}
                                    error={!!errors?.shipmentId}
                                    helperText={errors?.shipmentId?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item md={6}>
                        <Controller
                            name="driverId"
                            control={control}
                            render={({ field: { value } }) => (
                                <BaseAutocomplete
                                    fullWidth label="Driver" disabled
                                    value={value || ""}
                                    getOptionLabel={(o) => o ? `${o.driverId} - ${o.name}` : ""}
                                    options={drivers || []}
                                    loading={loadingDrivers}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item md={6}>
                        <Controller
                            name="vehicle_id"
                            control={control}
                            rules={{ required: "Vehicle is required." }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth label="Vehicle" required
                                    value={value || ""}
                                    onChange={(_, data) => onChange(data)}
                                    getOptionLabel={(o) =>
                                        o?.vehicle_id ? `${o.vehicle_id} - ${o.registration_number}` : ""
                                    }
                                    isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                    options={_watchDriver?.assigned_vehicle_ids || []}
                                    disabled={!_watchDriver}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </AppForm.Content>
            <AppForm.Actions>
                <Button onClick={() => navigate(-1)}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
                    {freeloadingDetail ? "Update" : "Add"} Free Loading
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};

export default DriverShipmentDestinationLoadingForm;